import { Navbar } from "../components/Navbar";
import { Link } from "react-router-dom";
import { FileText, AlertTriangle, CreditCard, Users, MessageSquare, Scale } from "lucide-react";

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-sm border p-8 sm:p-12">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#004E64]/10 rounded-full mb-4">
              <FileText className="w-8 h-8 text-[#004E64]" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900">Termini e Condizioni</h1>
            <p className="text-slate-500 mt-2">
              Condizioni generali di utilizzo del servizio LavoroTicino.ch
            </p>
            <p className="text-xs text-slate-400 mt-2">
              Versione 1.0 - Gennaio 2026
            </p>
          </div>

          {/* Content */}
          <div className="space-y-8">
            
            {/* Art. 1 - Definizioni */}
            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center text-sm">1</span>
                Definizioni
              </h2>
              <ul className="text-slate-700 space-y-2 text-sm">
                <li><strong>"Piattaforma":</strong> il sito web LavoroTicino.ch e i relativi servizi</li>
                <li><strong>"Titolare":</strong> Darko Vidakovic, titolare della piattaforma</li>
                <li><strong>"Utente":</strong> qualsiasi persona che accede o utilizza la piattaforma</li>
                <li><strong>"Candidato":</strong> utente che cerca opportunità lavorative</li>
                <li><strong>"Azienda":</strong> utente che pubblica annunci di lavoro</li>
                <li><strong>"Fornitore di Servizi":</strong> utente che offre servizi professionali</li>
              </ul>
            </section>

            {/* Art. 2 - Oggetto */}
            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center text-sm">2</span>
                Oggetto del Servizio
              </h2>
              <p className="text-slate-700 mb-3">
                LavoroTicino.ch è una <strong>piattaforma tecnologica di annunci</strong> che permette:
              </p>
              <ul className="list-disc list-inside text-slate-700 space-y-1 text-sm">
                <li>Alle aziende di pubblicare annunci di lavoro</li>
                <li>Ai candidati di creare un profilo professionale e candidarsi</li>
                <li>Ai fornitori di servizi di pubblicizzare le proprie prestazioni</li>
                <li>A tutti gli utenti di comunicare tramite la chat interna</li>
              </ul>
            </section>

            {/* Art. 3 - ESCLUSIONE MEDIAZIONE */}
            <section className="bg-red-50 border-2 border-red-200 rounded-xl p-6">
              <h2 className="text-xl font-bold text-red-800 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-6 h-6" />
                Art. 3 - ESCLUSIONE ATTIVITÀ DI MEDIAZIONE
              </h2>
              <div className="text-red-900 space-y-3 text-sm">
                <p>
                  <strong>LavoroTicino.ch è ESCLUSIVAMENTE una piattaforma tecnologica di annunci.</strong>
                </p>
                <p>
                  La piattaforma <strong>NON svolge in alcun modo</strong> attività di:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Mediazione del lavoro</li>
                  <li>Intermediazione</li>
                  <li>Collocamento</li>
                  <li>Selezione del personale</li>
                </ul>
                <p>
                  ai sensi della Legge federale sul Collocamento e sul personale a prestito 
                  (LC, RS 823.11) e della relativa Legge cantonale ticinese.
                </p>
                <p className="font-semibold mt-4 p-3 bg-red-100 rounded-lg">
                  Il contatto tra le parti avviene direttamente tramite la chat interna 
                  della piattaforma. Qualsiasi accordo, contratto o rapporto di lavoro 
                  viene stipulato esclusivamente tra le parti, senza alcuna partecipazione 
                  o responsabilità di LavoroTicino.ch.
                </p>
              </div>
            </section>

            {/* Art. 4 - Modello Business */}
            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <CreditCard className="w-6 h-6 text-slate-400" />
                Art. 4 - Modello di Business e Prezzi
              </h2>
              <div className="space-y-4">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <h3 className="font-bold text-emerald-800 mb-2">AZIENDE - GRATUITO</h3>
                  <p className="text-emerald-700 text-sm">
                    La pubblicazione di annunci di lavoro è <strong>completamente gratuita</strong> 
                    per le aziende. Non sono previsti costi nascosti o commissioni sulle assunzioni.
                  </p>
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="font-bold text-blue-800 mb-2">CANDIDATI - ABBONAMENTO</h3>
                  <p className="text-blue-700 text-sm mb-2">
                    I candidati possono consultare gratuitamente gli annunci. 
                    Per <strong>inviare candidature e messaggi</strong> alle aziende è richiesto 
                    un abbonamento:
                  </p>
                  <p className="text-blue-900 font-semibold">
                    CHF 24/anno (promozione lancio: CHF 12/anno)
                  </p>
                </div>
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <h3 className="font-bold text-amber-800 mb-2">FORNITORI DI SERVIZI - ABBONAMENTO</h3>
                  <p className="text-amber-700 text-sm mb-2">
                    Per pubblicare annunci di servizi e ricevere contatti dai clienti 
                    è richiesto un abbonamento:
                  </p>
                  <p className="text-amber-900 font-semibold">
                    CHF 48/anno (promozione lancio: CHF 24/anno)
                  </p>
                </div>
              </div>
            </section>

            {/* Art. 5 - Registrazione */}
            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Users className="w-6 h-6 text-slate-400" />
                Art. 5 - Registrazione e Requisiti
              </h2>
              <div className="space-y-3 text-slate-700 text-sm">
                <p>
                  <strong>5.1 Età minima:</strong> L'utilizzo della piattaforma è riservato a 
                  persone di età pari o superiore a <strong>18 anni</strong>.
                </p>
                <p>
                  <strong>5.2 Veridicità:</strong> L'utente garantisce che tutte le informazioni 
                  fornite durante la registrazione e l'utilizzo del servizio sono veritiere, 
                  accurate e aggiornate.
                </p>
                <p>
                  <strong>5.3 Account personale:</strong> Ogni utente può possedere un solo 
                  account. È vietata la cessione o condivisione delle credenziali di accesso.
                </p>
                <p>
                  <strong>5.4 Sicurezza:</strong> L'utente è responsabile della sicurezza delle 
                  proprie credenziali e di tutte le attività svolte con il proprio account.
                </p>
              </div>
            </section>

            {/* Art. 6 - Responsabilità */}
            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Scale className="w-6 h-6 text-slate-400" />
                Art. 6 - Limitazioni di Responsabilità
              </h2>
              <div className="space-y-3 text-slate-700 text-sm">
                <p>
                  <strong>6.1</strong> Gli utenti sono gli <strong>unici responsabili</strong> della 
                  veridicità delle informazioni inserite nei propri profili e annunci.
                </p>
                <p>
                  <strong>6.2</strong> La piattaforma <strong>non verifica</strong> l'identità, 
                  le qualifiche, le competenze o le referenze dichiarate dagli utenti.
                </p>
                <p>
                  <strong>6.3</strong> LavoroTicino.ch <strong>non è responsabile</strong> per:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Contenuti pubblicati dagli utenti</li>
                  <li>Accordi o contratti tra utenti</li>
                  <li>Qualità dei servizi offerti</li>
                  <li>Danni derivanti dall'utilizzo della piattaforma</li>
                  <li>Interruzioni temporanee del servizio</li>
                </ul>
                <p>
                  <strong>6.4</strong> Si raccomanda agli utenti di effettuare le proprie 
                  verifiche prima di stipulare qualsiasi accordo.
                </p>
              </div>
            </section>

            {/* Art. 7 - Comportamento */}
            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <MessageSquare className="w-6 h-6 text-slate-400" />
                Art. 7 - Regole di Comportamento
              </h2>
              <p className="text-slate-700 text-sm mb-3">
                È vietato utilizzare la piattaforma per:
              </p>
              <ul className="list-disc list-inside text-slate-700 space-y-1 text-sm">
                <li>Pubblicare contenuti falsi, ingannevoli o discriminatori</li>
                <li>Inviare spam o messaggi non richiesti</li>
                <li>Raccogliere dati di altri utenti per scopi non autorizzati</li>
                <li>Pubblicare offerte di lavoro illegali o non conformi alla legge</li>
                <li>Utilizzare la piattaforma per attività fraudolente</li>
                <li>Violare i diritti di proprietà intellettuale di terzi</li>
              </ul>
              <p className="text-slate-600 text-sm mt-3">
                La violazione di queste regole comporta la sospensione o cancellazione 
                dell'account senza rimborso.
              </p>
            </section>

            {/* Art. 8 - Pagamenti */}
            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-4">
                Art. 8 - Pagamenti e Rimborsi
              </h2>
              <div className="space-y-3 text-slate-700 text-sm">
                <p>
                  <strong>8.1</strong> I pagamenti vengono elaborati tramite Stripe in modo sicuro.
                </p>
                <p>
                  <strong>8.2</strong> Gli abbonamenti hanno durata annuale e si rinnovano 
                  automaticamente salvo disdetta.
                </p>
                <p>
                  <strong>8.3</strong> È possibile disdire l'abbonamento in qualsiasi momento. 
                  La disdetta avrà effetto alla scadenza del periodo già pagato.
                </p>
                <p>
                  <strong>8.4</strong> Non sono previsti rimborsi per abbonamenti già attivati, 
                  salvo casi eccezionali a discrezione del Titolare.
                </p>
              </div>
            </section>

            {/* Art. 9 - Modifiche */}
            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-4">
                Art. 9 - Modifiche ai Termini
              </h2>
              <p className="text-slate-700 text-sm">
                Il Titolare si riserva il diritto di modificare i presenti Termini e Condizioni. 
                Le modifiche verranno comunicate tramite email o avviso sul sito con almeno 
                30 giorni di preavviso. L'utilizzo continuato del servizio dopo l'entrata 
                in vigore delle modifiche costituisce accettazione delle stesse.
              </p>
            </section>

            {/* Art. 10 - Legge e Foro */}
            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-4">
                Art. 10 - Legge Applicabile e Foro Competente
              </h2>
              <div className="bg-slate-50 p-4 rounded-lg text-sm">
                <p className="text-slate-700 mb-2">
                  I presenti Termini e Condizioni sono regolati dal <strong>diritto svizzero</strong>.
                </p>
                <p className="text-slate-700">
                  Per qualsiasi controversia derivante dall'utilizzo della piattaforma, 
                  sarà competente in via esclusiva il <strong>Foro di Lugano, Svizzera</strong>.
                </p>
              </div>
            </section>

            {/* Art. 11 - Contatti */}
            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-4">
                Art. 11 - Contatti
              </h2>
              <p className="text-slate-700 text-sm mb-3">
                Per qualsiasi domanda relativa ai presenti Termini:
              </p>
              <div className="bg-slate-50 p-4 rounded-lg text-sm">
                <p className="text-slate-700"><strong>Email:</strong> info@lavoroticino.ch</p>
                <p className="text-slate-700 mt-1"><strong>Telefono:</strong> +41 78 614 18 54</p>
                <p className="text-slate-700 mt-1"><strong>Indirizzo:</strong> 6945 Origlio, Ticino, Svizzera</p>
              </div>
            </section>
          </div>

          {/* Acceptance */}
          <div className="mt-10 p-4 bg-slate-100 rounded-lg text-center">
            <p className="text-slate-600 text-sm">
              Utilizzando LavoroTicino.ch, l'utente dichiara di aver letto, compreso e 
              accettato integralmente i presenti Termini e Condizioni.
            </p>
          </div>

          {/* Footer links */}
          <div className="mt-8 pt-6 border-t flex flex-wrap gap-4 text-sm">
            <Link to="/impressum" className="text-[#004E64] hover:underline">Impressum</Link>
            <Link to="/privacy" className="text-[#004E64] hover:underline">Privacy Policy</Link>
            <Link to="/" className="text-slate-500 hover:text-slate-700">← Torna alla home</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
